import React, { useState } from 'react';
import {
  User,
  Shield,
  Bell,
  Building,
  KeyRound,
  CheckCircle2,
  Mail,
  Phone,
  Save,
  AlertCircle,
  Lock,
  FileText,
  Smartphone,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';

export const AccountSettingsView: React.FC = () => {
  const { currentUser, isEmailVerified, sendVerificationEmail } = useAuth();
  const { activeSite, updateSiteData } = useSite();

  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'billing'>('profile');

  // Profile state
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [phone, setPhone] = useState(currentUser?.phone || activeSite?.settings?.phone || '');
  const [companyName, setCompanyName] = useState(currentUser?.companyName || activeSite?.settings?.companyName || activeSite?.settings?.businessName || activeSite?.name || '');
  const [sector, setSector] = useState(currentUser?.sector || activeSite?.industry || 'Bilişim & Hizmet');

  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Notifications state
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(true);
  const [notifyMarketing, setNotifyMarketing] = useState(false);

  // Billing / Invoice state
  const [taxOffice, setTaxOffice] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState(activeSite?.settings?.address || '');

  // UI status
  const [savedSuccess, setSavedSuccess] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMsg(null);

    try {
      if (activeSite) {
        updateSiteData({
          settings: {
            ...(activeSite.settings || {}),
            companyName,
            businessName: companyName,
            phone,
            address: invoiceAddress,
          },
        });
      }
      setSavedSuccess('Profil ve iletişim bilgileriniz başarıyla güncellendi.');
      setTimeout(() => setSavedSuccess(null), 4000);
    } catch (err: any) {
      setErrorMsg(err.message || 'Güncelleme yapılırken bir sorun oluştu.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!newPassword || newPassword.length < 6) {
      setErrorMsg('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Yeni şifreler birbiriyle eşleşmiyor.');
      return;
    }

    setSavedSuccess('Şifreniz başarıyla güncellendi.');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setSavedSuccess(null), 4000);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6 sm:space-y-8 animate-in fade-in select-none">
      {/* Top Header */}
      <div className="pb-6 border-b border-slate-200">
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">AYARLAR</h1>
        <p className="text-sm text-slate-500 mt-1">
          Hesap profiliniz, güvenlik ayarlarınız, bildirim tercihleriniz ve kurumsal fatura bilgileriniz.
        </p>
      </div>

      {/* Success / Error Alerts */}
      {savedSuccess && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{savedSuccess}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-1">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition flex items-center gap-2 ${
            activeTab === 'profile'
              ? 'bg-blue-600 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <User className="w-4 h-4" />
          Profil & Yetkili
        </button>

        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition flex items-center gap-2 ${
            activeTab === 'security'
              ? 'bg-blue-600 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Shield className="w-4 h-4" />
          Güvenlik & Şifre
        </button>

        <button
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition flex items-center gap-2 ${
            activeTab === 'notifications'
              ? 'bg-blue-600 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Bell className="w-4 h-4" />
          Bildirim Tercihleri
        </button>

        <button
          onClick={() => setActiveTab('billing')}
          className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition flex items-center gap-2 ${
            activeTab === 'billing'
              ? 'bg-blue-600 text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Building className="w-4 h-4" />
          Firma & Fatura
        </button>
      </div>

      {/* Content Area */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
        {/* TAB 1: PROFILE */}
        {activeTab === 'profile' && (
          <form onSubmit={handleSaveProfile} className="space-y-6 max-w-2xl">
            <div>
              <h3 className="text-base font-bold text-slate-900">Yetkili ve İletişim Bilgileri</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Hesap yöneticisinin adı, birincil e-posta adresi ve SMS bildirim numarası.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Yetkili Adı Soyadı</label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Örn: Ahmet Yılmaz"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">E-posta Adresi</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="email"
                    disabled
                    value={currentUser?.email || ''}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-xs font-medium text-slate-500 cursor-not-allowed"
                  />
                </div>
                <span className="text-[10px] text-slate-400 mt-1 block">
                  E-posta değişikliği için güvenlik teyidi gereklidir.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Yetkili Telefon Numarası</label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="+90 5XX XXX XX XX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Faaliyet Gösterilen Sektör</label>
                <input
                  type="text"
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Örn: Turizm, Danışmanlık, İmalat"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-md shadow-blue-500/20 transition flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</span>
              </button>
            </div>
          </form>
        )}

        {/* TAB 2: SECURITY */}
        {activeTab === 'security' && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <h3 className="text-base font-bold text-slate-900">Güvenlik ve Kimlik Doğrulama</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Şifrenizi düzenli aralıklarla değiştirmeniz hesap güvenliğinizi artırır.
              </p>
            </div>

            {/* Email verification status card */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-slate-900 flex items-center gap-2">
                  <span>E-posta Doğrulama Durumu:</span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      isEmailVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {isEmailVerified ? '✓ Doğrulanmış Hesap' : '⚠ Henüz Doğrulanmadı'}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {isEmailVerified
                    ? 'E-posta adresiniz onaylıdır ve şifre sıfırlama işlemleri güvendedir.'
                    : 'Hesabınızın güvenliği için lütfen gelen kutunuzdaki bağlantıya tıklayarak e-postanızı doğrulayınız.'}
                </div>
              </div>

              {!isEmailVerified && (
                <button
                  onClick={sendVerificationEmail}
                  className="px-3.5 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold transition"
                >
                  Doğrulama Gönder
                </button>
              )}
            </div>

            {/* Password Form */}
            <form onSubmit={handleUpdatePassword} className="space-y-4 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Mevcut Şifre</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Yeni Şifre</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="En az 6 karakter"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Yeni Şifre (Tekrar)</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="pt-3 flex justify-end">
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition flex items-center gap-2"
                >
                  <KeyRound className="w-4 h-4" />
                  <span>Şifreyi Güncelle</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* TAB 3: NOTIFICATIONS */}
        {activeTab === 'notifications' && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <h3 className="text-base font-bold text-slate-900">Bildirim ve Uyarı Tercihleri</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Sitenize gelen yeni mesajlar, talepler ve abonelik bitiş uyarıları için bildirim kanallarını belirleyin.
              </p>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 p-4 rounded-2xl border border-slate-200 hover:bg-slate-50 transition cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 mt-0.5"
                />
                <div>
                  <div className="text-xs font-bold text-slate-900">E-posta Bildirimleri</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Web sitenizin iletişim formundan gelen mesajlar ve hizmet talebi güncellemeleri kayıtlı e-postanıza iletilsin.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-2xl border border-slate-200 hover:bg-slate-50 transition cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifySms}
                  onChange={(e) => setNotifySms(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 mt-0.5"
                />
                <div>
                  <div className="text-xs font-bold text-slate-900">SMS / WhatsApp Uyarıları</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Kritik alan adı yenileme ve acil rezervasyon/teklif talepleri telefonunuza SMS veya WhatsApp bildirimi olarak gelsin.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-2xl border border-slate-200 hover:bg-slate-50 transition cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyMarketing}
                  onChange={(e) => setNotifyMarketing(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 mt-0.5"
                />
                <div>
                  <div className="text-xs font-bold text-slate-900">Adiosoft Ürün ve Özellik Bülteni</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Yeni altyapı ve lisans özellikleri, SEO güncellemeleri ve platform yenilikleri hakkında aylık bilgilendirme.
                  </div>
                </div>
              </label>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setSavedSuccess('Bildirim tercihleriniz güncellendi.');
                  setTimeout(() => setSavedSuccess(null), 3000);
                }}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>Tercihleri Kaydet</span>
              </button>
            </div>
          </div>
        )}

        {/* TAB 4: BILLING & INVOICE */}
        {activeTab === 'billing' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSavedSuccess('Fatura ve vergi bilgileriniz kaydedildi.');
              setTimeout(() => setSavedSuccess(null), 3000);
            }}
            className="space-y-6 max-w-2xl"
          >
            <div>
              <h3 className="text-base font-bold text-slate-900">Kurumsal Fatura ve Vergi Bilgileri</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Shopier veya banka havalesi üzerinden yapılan ödemelerde e-faturanızın düzenleneceği şirket bilgileri.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Resmi Şirket Unvanı</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Örn: ABC Turizm ve Ticaret Ltd. Şti."
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Vergi Dairesi</label>
                <input
                  type="text"
                  value={taxOffice}
                  onChange={(e) => setTaxOffice(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Örn: Kadıköy V.D."
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Vergi Numarası / T.C. Kimlik No</label>
                <input
                  type="text"
                  value={taxNumber}
                  onChange={(e) => setTaxNumber(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="10 veya 11 haneli numara"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Fatura Adresi</label>
                <textarea
                  rows={3}
                  value={invoiceAddress}
                  onChange={(e) => setInvoiceAddress(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Cadde, mahalle, kapı no, ilçe ve şehir"
                />
              </div>
            </div>

            <div className="pt-3 flex justify-end">
              <button
                type="submit"
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>Fatura Bilgilerini Kaydet</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
